import React from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";

import { FieldErrors } from "src/api/types";
import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { ReorderHandle } from "src/base-components/ReorderHandle";
import { ClauseBlock, ClauseForm } from "src/clauseBlock/ClauseBlock";
import { Input } from "src/design-system/Input";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { SplitBranchWithName } from "src/nodeEditor/splitNodeSuggestionUtils";
import { SplitNodeV2Form } from "src/splitNodeV2Editor/SplitNodeV2Editor";
import { useGraphStore } from "src/store/StoreProvider";
import { logger } from "src/utils/logger";
import { useHighlightedScrollIntoView } from "src/utils/useHighlightedScrollIntoview";

type Props = {
  immutable: boolean;
  onDeleteRequest: () => void;
  index: number;
  branch: SplitBranchWithName;
  fieldErrors: FieldErrors | undefined;
};

export const BranchCard: React.FC<Props> = ({
  immutable,
  onDeleteRequest,
  index,
  branch,
  fieldErrors,
}) => {
  const { updateNode, getNodeToIncomingEdge } = useGraphStore();
  const { setValue, control } = useFormContext<SplitNodeV2Form>();

  const isBranchHighlighted = useIsFieldHighlighted(branch.edge_id);
  const [highlightRef] = useHighlightedScrollIntoView(isBranchHighlighted);

  const { listeners, setNodeRef, transform, transition } = useSortable({
    id: branch.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleChange = (value: ClauseForm) => {
    setValue(`branches.${index}.clauses`, value.clauses);
    setValue(`branches.${index}.junction`, value.junction);
  };

  const branchName = branch.name;

  const onChangeBranchName: React.FormEventHandler<HTMLInputElement> = (e) => {
    const branchNodeToRename = getNodeToIncomingEdge(branch.edge_id);
    if (branchNodeToRename) {
      updateNode({
        newName: e.currentTarget.value,
        nodeId: branchNodeToRename.id,
      });
    }
  };

  const onDeleteBranch = async () => {
    try {
      await onDeleteRequest();
    } catch {
      logger.error("Error deleting branch");
    }
  };

  return (
    <Card
      ref={setNodeRef}
      className="my-5 space-y-3"
      data-loc={`branch-${index}`}
      style={style}
    >
      <Card.Header>
        <div className="flex flex-row items-center gap-x-1">
          <ReorderHandle
            className="-ml-2"
            immutable={immutable}
            listeners={listeners}
          />
          <Card.Title title={`Branch ${index + 1}`} />
        </div>
        <Button
          dataLoc={`delete-split-branch-${index}`}
          disabled={immutable}
          iconLeft={faTrashAlt}
          size="sm"
          variant="secondary"
          onClick={onDeleteBranch}
        >
          Delete branch
        </Button>
      </Card.Header>
      <Card.Content>
        <Controller
          control={control}
          name={`branches.${index}`}
          render={({ field }) => (
            <ClauseBlock
              clauses={field.value.clauses}
              fieldErrors={fieldErrors}
              immutable={immutable}
              junction={field.value.junction}
              onChange={handleChange}
            />
          )}
        />
        <div className="flex flex-row items-center border-gray-200 border-t pt-3">
          <div className="mr-2 w-24 font-inter-medium-12px text-gray-700">
            Use branch
          </div>
          <div ref={highlightRef} className="flex-grow">
            <Input
              dataLoc={`branch-${index}-name-input`}
              defaultValue={branchName}
              disabled={immutable}
              highlighted={isBranchHighlighted}
              variant="monospaced"
              onChange={onChangeBranchName}
            />
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};
